import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import React from "react";

type ChartData = {
  name: string;
  value: number;
};

interface PieChartComponentProps {
  title: string;
  data: ChartData[];
  colordata: string[]; // Array of color hex codes (without `#`)
  innerRadius?: number;
  outerRadius?: number;
}

// Label inside each slice
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  value,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 0.7;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={13}
      fontWeight={700}
      style={{
        textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
      }}
    >
      {value}
    </text>
  );
};

// Custom Tooltip
const CustomTooltip = ({
  active,
  payload,
  colordata,
  data,
}: {
  active?: boolean;
  payload?: any;
  colordata: string[];
  data: ChartData[];
}) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0].payload;
    const index = data.findIndex((item) => item.name === name);
    const bgColor = `#${colordata[index % colordata.length]}`;

    return (
      <div
        className="rounded px-3 py-1 text-sm text-white font-medium shadow"
        style={{
          backgroundColor: bgColor,
          border: `1px solid ${bgColor}`,
          minWidth: "120px",
          textAlign: "center",
        }}
      >
        <div className="font-bold">{name}</div>
        <div>{value.toLocaleString()}</div>
      </div>
    );
  }
  return null;
};

export default function PieCharts({
  title,
  data,
  colordata,
  innerRadius = 0,
  outerRadius = 100,
}: PieChartComponentProps) {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return (
      <div className="bg-white p-4 rounded-xl shadow">
        <div className="text-red-500">Chart failed to load</div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      {/* Title */}
      <h2 className="text-sm font-semibold text-gray-600 mb-4">{title}</h2>

      {/* Chart Box */}
      <div className="bg-white pt-8 px-4 pb-4 rounded-xl shadow w-full">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              labelLine={false}
              label={renderCustomizedLabel}
              paddingAngle={2}
              animationDuration={1000}
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`#${colordata[index % colordata.length]}`}
                  stroke={`#${colordata[index % colordata.length]}`}
                  strokeWidth={1}
                />
              ))}
            </Pie>

            {/* Custom Tooltip */}
            <Tooltip content={<CustomTooltip colordata={colordata} data={data} />} />

            {/* Legend */}
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={10}
              wrapperStyle={{
                marginTop: 20,
                paddingTop: 10,
                fontSize: "13px",
                color: "#555",
                fontWeight: 500,
                lineHeight: "20px",
                textAlign: "center",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}



